/**
 * Общата основа на оркестратора на резерва (`edgeMode.ts` — режимът и
 * сондите; `edgeSession.ts` — конфигурация, билет, таймери): ръчките към
 * Redux store-а и React Query, текущият токен/билет и ЕДИНСТВЕНИТЕ писачи
 * на режима и конфигурацията — те държат в синхрон Redux `edgeSlice` (за
 * UI-я), `edgeState.ts` (за интерсепторите) и кеша `edge_mode` (за студен
 * старт). Нищо тук не прави заявки.
 */
import type { QueryClient } from "@tanstack/react-query";
import type { AppDispatch, RootState } from "../../store/store";
import {
  setEdgeConfig,
  setEdgeMode,
  setEdgeRestricted,
  setThisDeviceIsTill,
} from "../../store/edgeSlice";
import { getToken } from "../auth";
import { rememberEdgeLastKnown } from "./edgeLastKnown";
import { writeCachedEdgeMode } from "./edgeModeCache";
import {
  getEdgeConfigState,
  isEdgeLocalMode,
  isThisDeviceTheTill,
  setEdgeConfigState,
  setEdgeModeState,
  type EdgeConfig,
  type EdgeMode,
} from "./edgeState";
import { getEdgeTicketForToken } from "./edgeTicket";

export interface EdgeStore {
  getState: () => RootState;
  dispatch: AppDispatch;
  subscribe: (listener: () => void) => () => void;
}

export interface EdgeRuntime {
  store: EdgeStore;
  queryClient: QueryClient;
}

let runtime: EdgeRuntime | null = null;

export function setEdgeRuntime(store: EdgeStore, queryClient: QueryClient) {
  runtime = { store, queryClient };
}

export const getEdgeRuntime = (): EdgeRuntime | null => runtime;

export const devLog = (...args: unknown[]) => {
  if (import.meta.env.DEV) console.debug("[edge]", ...args);
};

/**
 * Предупреждение, което остава и в production (и става breadcrumb в Sentry) —
 * за решенията, които ЗАОБИКАЛЯТ проверка: връщане в облака без отговор от
 * касата (§15.5). Такова решение трябва да се вижда в лога на терминала,
 * когато после някой пита защо масите са се разминали.
 */
export const warnLog = (...args: unknown[]) => console.warn("[edge]", ...args);

/** Токенът, с който в момента тръгват заявките (както axios интерсептора). */
export const currentToken = (): string | null =>
  runtime ? getToken(runtime.store.getState().auth.token) : null;

/** Билетът, който върви с текущия токен (виж `edgeTicket.ts`). */
export const getActiveEdgeTicket = (): string | null =>
  getEdgeTicketForToken(currentToken());

export function applyConfig(config: EdgeConfig | null) {
  setEdgeConfigState(config);
  runtime?.store.dispatch(setEdgeConfig(config));
  // Сменена главна каса (друг `masterClientId`) връща устройството към
  // обявения адрес — панелът го научава веднага, не чак при следващата сонда.
  runtime?.store.dispatch(setThisDeviceIsTill(isThisDeviceTheTill()));
  // §15.4: адресът на касата преживява изхода в отделен ключ — той не е нито
  // билет, нито самоличност (`edgeLastKnown.ts`). Празна конфигурация не
  // изтрива запомненото: „не знаем" не е „няма каса".
  if (config) rememberEdgeLastKnown(config);
}

/**
 * Ограничен локален режим (§14.3.1): срещу касата сме, но без билет за
 * активния токен — тя приема само `POST {edge}/user/login` и
 * `POST {edge}/user/login-with-code`, а всичко останало води до екрана за
 * вход. Билетът идва с локалния вход (самият локален `access_token`), затова
 * състоянието се преизчислява при всяка смяна на режима И на токена.
 */
export const isEdgeRestrictedNow = (): boolean =>
  isEdgeLocalMode() && getActiveEdgeTicket() === null;

/** Изравнява Redux с `isEdgeRestrictedNow()` (UI-ят чете само оттам). */
export function syncEdgeRestricted() {
  runtime?.store.dispatch(setEdgeRestricted(isEdgeRestrictedNow()));
}

/** Смяна на режима: edgeState (интерсепторите), Redux (UI) и кешът за
 * студен старт (`localStorage["edge_mode"]`, docs/edge.md). */
export function setMode(mode: EdgeMode, localSince: string | null) {
  setEdgeModeState(mode);
  runtime?.store.dispatch(setEdgeMode({ mode, localSince }));
  syncEdgeRestricted();
  const config = getEdgeConfigState();
  writeCachedEdgeMode({
    mode,
    localSince: mode === "local" ? localSince : null,
    ip: config?.ip ?? null,
    port: config?.port ?? 8787,
  });
}
